import React, { useEffect, useState } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import { useHistory, Link } from "react-router-dom";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { HeaderTitle } from "../common/titles";
import { has_access } from "../../../middleware/privileges";
import { GET_INVENTORY_INGREDIENTS, GET_LOW_STOCK_INGREDIENTS, GET_BRANCHES } from "./queries";
import {
  InventoryIngredient,
  CATEGORY_OPTIONS,
  CATEGORY_LABELS,
} from "./models";
import InventoryNav from "./inventoryNav";
import { generateCsvTemplate, generateCsvExport, downloadCsv } from "./csvUtils";
import CsvImportModal from "./csvImportModal";

const btn = {
  txt: lang("Nuevo Ingrediente", "New Ingredient"),
  icon: "fa fa-plus",
  link: "/inventory/new",
  dataTour: "new-ingredient-btn",
};

const IngredientList: React.FC = () => {
  const history = useHistory();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [fetchIngredients, { data, loading }] = useLazyQuery(
    GET_INVENTORY_INGREDIENTS,
    { fetchPolicy: "no-cache" }
  );
  const [fetchLowStock, { data: lowStockData }] = useLazyQuery(
    GET_LOW_STOCK_INGREDIENTS,
    { fetchPolicy: "no-cache" }
  );
  const [showImport, setShowImport] = useState(false);
  const { data: branchesData } = useQuery(GET_BRANCHES);

  useEffect(() => {
    fetchLowStock();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchIngredients({
      variables: {
        search: search || undefined,
        category: category || undefined,
        take: 100,
      },
    });
    //eslint-disable-next-line
  }, [search, category]);

  const lowStockCount = lowStockData?.getLowStockIngredients?.length || 0;

  const getStockClass = (ingredient: InventoryIngredient): string => {
    const stock = ingredient.total_stock || 0;
    const threshold = ingredient.low_stock_threshold || 0;
    if (threshold <= 0) return "";
    if (stock <= 0) return "text-danger font-weight-bold";
    if (stock <= threshold) return "text-warning font-weight-bold";
    return "text-success";
  };

  return (
    <>
      <HeaderTitle
        title={lang("Inventario", "Inventory")}
        btn={has_access(256) ? btn : null}
      />
      <InventoryNav active="ingredients" />

      {lowStockCount > 0 && (
        <div className="alert alert-warning d-flex align-items-center mb-3">
          <i className="fa fa-exclamation-triangle mr-2" />
          <span>
            <strong>{lowStockCount}</strong>{" "}
            {lang(
              "ingrediente(s) con stock bajo o agotado.",
              "ingredient(s) with low or zero stock."
            )}
          </span>
          <Link
            to="/inventory/alerts"
            className="btn btn-warning btn-sm ml-auto"
          >
            {lang("Ver Alertas", "View Alerts")}
          </Link>
        </div>
      )}

      <div className="row mb-3">
        <div className="col-lg-4">
          <input
            type="text"
            className="form-control"
            placeholder={lang("Buscar ingrediente...", "Search ingredient...")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="col-lg-3">
          <select
            className="custom-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">{lang("Todas las categorías", "All categories")}</option>
            {CATEGORY_OPTIONS.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {lang(cat.label, cat.labelEn)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-lg-12 d-flex justify-content-end">
          <button
            className="btn btn-outline-secondary btn-sm mr-2"
            onClick={() => {
              const branches = branchesData?.getBranches || [];
              downloadCsv(
                generateCsvTemplate(branches),
                "plantilla_inventario.csv"
              );
            }}
          >
            <i className="fa fa-download mr-1" />
            {lang("Plantilla CSV", "CSV Template")}
          </button>
          <button
            className="btn btn-outline-secondary btn-sm mr-2"
            onClick={() => {
              const branches = branchesData?.getBranches || [];
              const ingredients = data?.getInventoryIngredients || [];
              downloadCsv(
                generateCsvExport(ingredients, branches),
                "inventario.csv"
              );
            }}
          >
            <i className="fa fa-download mr-1" />
            {lang("Descargar Inventario", "Download Inventory")}
          </button>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => setShowImport(true)}
          >
            <i className="fa fa-upload mr-1" />
            {lang("Importar CSV", "Import CSV")}
          </button>
        </div>
      </div>

      {loading && <LoaderDots fullSize />}

      {data && (
        <div className="row">
          <div className="col-lg-12">
            <div className="material_card p-3 table-responsive">
              <table className="table table-hover vertical_middle">
                <thead className="thead-light">
                  <tr>
                    <th>{lang("Nombre", "Name")}</th>
                    <th>SKU</th>
                    <th>{lang("Unidad", "Unit")}</th>
                    <th>{lang("Costo", "Cost")}</th>
                    <th>{lang("Categoría", "Category")}</th>
                    <th>{lang("Stock Total", "Total Stock")}</th>
                    <th>{lang("Estado", "Status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.getInventoryIngredients.map(
                    (ingredient: InventoryIngredient) => (
                      <tr
                        key={ingredient.id}
                        className="cursor-pointer"
                        onClick={() =>
                          history.push(`/inventory/edit/${ingredient.id}`)
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <td className="font-weight-bold">{ingredient.name}</td>
                        <td>
                          <small className="text-muted">
                            {ingredient.sku || "—"}
                          </small>
                        </td>
                        <td>{ingredient.unit}</td>
                        <td>
                          {ingredient.cost
                            ? `$${Number(ingredient.cost).toFixed(2)}`
                            : "—"}
                        </td>
                        <td>
                          {ingredient.category ? (
                            <span className="badge badge-secondary">
                              {lang(
                                CATEGORY_LABELS[ingredient.category]?.label || ingredient.category,
                                CATEGORY_LABELS[ingredient.category]?.labelEn || ingredient.category
                              )}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className={getStockClass(ingredient)}>
                          {ingredient.total_stock != null
                            ? `${Number(ingredient.total_stock).toFixed(2)} ${ingredient.unit}`
                            : "—"}
                        </td>
                        <td>
                          <span
                            className={`badge badge-${
                              ingredient.status ? "success" : "secondary"
                            }`}
                          >
                            {ingredient.status
                              ? lang("Activo", "Active")
                              : lang("Inactivo", "Inactive")}
                          </span>
                        </td>
                      </tr>
                    )
                  )}
                  {data.getInventoryIngredients.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        {lang(
                          "No hay ingredientes registrados",
                          "No ingredients registered"
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <CsvImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onSuccess={() => {
          setShowImport(false);
          fetchIngredients({
            variables: {
              search: search || undefined,
              category: category || undefined,
              take: 100,
            },
          });
        }}
      />
    </>
  );
};

export default IngredientList;
